import React from 'react';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { SEARCH_DELAY, VIEWS } from '../Transactions/TransactionsConstants';
import CashflowSummary from './Components/CashflowSummary';
import ListControls from './Components/ListControls';
import MobileEditTransaction from './Components/MobileEditTransaction';
import MobileSearch from './Components/MobileSearch';
import TransactionsList from './Components/TransactionsList';
import SectionToggle from '../MobileNetworthTransactions/Components/SectionToggle';
import { StyledMobileTopNavSmallView } from '../MobileNetworthTransactions/MobileNetWorthTransactionsStyles';
import {
  ControlsWrapper,
  Wrapper,
  EmptyStateWrapper,
  EmptyStateIllustration,
  EmptyStateText,
  AddButton,
} from './MobileTransactionsStyles';

class MobileTransactionsListView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchBarVisible: false,
    };

    this.fetchNextPage = this.fetchNextPage.bind(this);
    this.fetchFilteredResults = this.fetchFilteredResults.bind(this);
    this.showSearchBar = this.showSearchBar.bind(this);
    this.hideSearchBar = this.hideSearchBar.bind(this);
    this.handleQueryChange = this.handleQueryChange.bind(this);
    this.debouncedSearch = debounce(query => this.fetchFilteredResults({ query }), SEARCH_DELAY);
  }

  componentDidMount() {
    const { currentView, portalName } = this.props;

    document.title = `Transactions | ${portalName}`;

    if (currentView === VIEWS.LIST) {
      const currentHash = ADV.util.Router.getObjFromHash(ADV.util.getHash());

      const { accountId } = currentHash.params || '';

      this.fetchFilteredResults({ accountId: accountId === '0' ? '' : accountId });
    }
  }

  handleQueryChange(query) {
    const { setQuery } = this.props;

    setQuery(query);
    this.debouncedSearch(query);
  }

  showSearchBar() {
    this.setState({ isSearchBarVisible: true });
  }

  hideSearchBar() {
    this.setState({ isSearchBarVisible: false });
    this.handleQueryChange('');
  }

  fetchNextPage() {
    const {
      pagination: { currentPage, totalPagesCount },
    } = this.props;

    if (totalPagesCount > currentPage) {
      this.fetchFilteredResults({}, currentPage + 1);
    }
  }

  fetchFilteredResults(filters, page = 1) {
    const {
      fetchTransactions,
      clientId,
      currentAccount,
      currentPeriodId,
      currentQuery,
      expenseCategoryFilter,
      selectCategory,
    } = this.props;

    const accountId = isNil(filters.accountId) ? currentAccount.accountId : filters.accountId;
    const periodId = isNil(filters.periodId) ? currentPeriodId : filters.periodId;
    const query = isNil(filters.query) ? currentQuery : filters.query;
    const category = isNil(filters.expenseCategory)
      ? expenseCategoryFilter
      : filters.expenseCategory;

    selectCategory(category);
    fetchTransactions(clientId, accountId, periodId, query, category, page);
  }

  render() {
    const {
      clientId,
      currentPeriodId,
      transactions,
      expenseCategoryOptions,
      periodOptions,
      accounts,
      currentAccount,
      setDraftTransaction,
      currentQuery,
      draftTransaction,
      updateDraftTransaction,
      pagination: { currentPage, totalPagesCount },
      expensesData,
      incomeData,
      currentView,
      setCurrentView,
      saveDraftTransaction,
      expenseCategoryFilter,
      currentTab,
      setCurrentTab,
      scrollMobileToTop,
      addAccount,
      hasAccessToLinkAccounts,
      TABS,
    } = this.props;

    if (!transactions || !periodOptions || !accounts) {
      return null;
    }

    const hasTransactionsInAnyAccount =
      currentAccount.accountId === '' && currentPeriodId === 5 ? transactions.length > 0 : true;

    const isSearchBarVisible = this.state.isSearchBarVisible && currentView !== VIEWS.EDIT;

    return (
      <Wrapper>
        {!isSearchBarVisible && (
          <StyledMobileTopNavSmallView
            onClick={currentView === VIEWS.EDIT && (() => setCurrentView(VIEWS.LIST))}
            headerText={currentView === VIEWS.EDIT ? draftTransaction.description : 'Transactions'}
            smallFont={currentView === VIEWS.EDIT}
          />
        )}

        {!isSearchBarVisible && currentView === VIEWS.LIST && (
          <SectionToggle currentView={currentTab} onChange={setCurrentTab} views={TABS} />
        )}

        {isSearchBarVisible && (
          <MobileSearch
            query={currentQuery}
            onQueryChange={this.handleQueryChange}
            onSearchDoneClick={this.hideSearchBar}
          />
        )}

        {!isSearchBarVisible && currentView === VIEWS.LIST && hasTransactionsInAnyAccount && (
          <ControlsWrapper>
            <CashflowSummary
              income={incomeData ? incomeData.total : 0}
              expenses={expensesData ? expensesData.total : 0}
            />
            <ListControls
              accounts={accounts}
              periodOptions={periodOptions}
              currentAccountId={currentAccount.accountId}
              currentPeriodId={currentPeriodId}
              onAccountChange={({ accountId }) =>
                ADV.util.Router.go2('Transactions', { clientId, accountId })
              }
              onPeriodChange={this.fetchFilteredResults}
              onSearchClick={this.showSearchBar}
            />
          </ControlsWrapper>
        )}

        {currentView === VIEWS.EDIT && (
          <MobileEditTransaction
            scrollMobileToTop={scrollMobileToTop}
            transaction={draftTransaction}
            onChange={updateDraftTransaction}
            expenseCategoryOptions={expenseCategoryOptions}
            onSave={() =>
              saveDraftTransaction(
                clientId,
                draftTransaction,
                currentAccount.accountId,
                currentPeriodId,
                currentQuery,
                expenseCategoryFilter,
                currentPage,
              )
            }
          />
        )}

        {currentView === VIEWS.LIST && !hasTransactionsInAnyAccount && (
          <EmptyStateWrapper>
            <EmptyStateIllustration
              src={`${ADV.settings.endpoints.imgDir}Userportal-01.png`}
              alt="safe"
            />

            <EmptyStateText>
              There are no transactions within the accounts you have added.
            </EmptyStateText>

            <AddButton
              onClick={() => {
                setCurrentTab(TABS.NETWORTH);
                addAccount(hasAccessToLinkAccounts);
              }}
            >
              Add an Account
            </AddButton>
          </EmptyStateWrapper>
        )}

        {currentView === VIEWS.LIST && hasTransactionsInAnyAccount && (
          <TransactionsList
            transactions={transactions}
            currentPage={currentPage}
            totalPagesCount={totalPagesCount}
            fetchNextPage={this.fetchNextPage}
            currentAccount={currentAccount}
            currentPeriodId={currentPeriodId}
            onEditTransactionClick={transaction => {
              setDraftTransaction(transaction);
              setCurrentView(VIEWS.EDIT);
            }}
          />
        )}
      </Wrapper>
    );
  }
}

MobileTransactionsListView.defaultProps = {};

MobileTransactionsListView.propTypes = {
  portalName: PropTypes.string.isRequired,
};

export default MobileTransactionsListView;
